import { Carte, Message, Squelette, Vide } from '@journal/ui';

/**
 * Réglages → Matières & Domaines.
 *
 * Reprend `renderMatieres` de l'application d'origine, y compris sa nature :
 * un écran de CONSULTATION, dont la seule action est le choix de la couleur.
 *
 * ── Pourquoi rien ne se crée ici ────────────────────────────────────────────
 * Les matières et leurs domaines viennent du programme officiel. L'écran
 * précédent permettait d'en créer et d'en supprimer — c'était une invention :
 * les fonctions correspondantes existent bien dans le fichier d'origine, mais
 * ne sont appelées par aucun bouton. Créer une matière hors référentiel
 * produirait une matière sans objectifs, donc inévaluable, et invisible dans
 * l'écran Programme.
 *
 * La couleur, elle, est bien un choix de l'enseignant : elle sert à distinguer
 * les matières dans l'emploi du temps et le cahier journal.
 */

export interface DomaineDuProgramme {
  id: string;
  name: string;
  subject: string;
  color: string;
  subdomains: Array<{ id: string; name: string }>;
}

export function ReglagesMatieres({
  domaines, chargement, couleurs, niveaux, surCouleur,
}: {
  domaines: DomaineDuProgramme[] | null;
  chargement: boolean;
  /** Couleurs choisies par l'école, par nom de matière. */
  couleurs: Record<string, string>;
  /** Niveaux enseignés, pour rappeler le filtre appliqué. */
  niveaux: string;
  surCouleur: (matiere: string, couleur: string) => void;
}) {
  if (chargement && !domaines) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Squelette className="h-64" />
        <Squelette className="h-64" />
        <Squelette className="h-64" />
      </div>
    );
  }

  if (!domaines || domaines.length === 0) {
    return (
      <Vide titre="Aucun programme disponible">
        Le référentiel officiel n'est pas chargé pour vos niveaux. Vérifiez les
        niveaux de la classe dans l'onglet Général.
      </Vide>
    );
  }

  /*
   * Regroupement par matière, puis par NOM de domaine.
   *
   * Par matière, parce que le référentiel liste des domaines et que l'écran
   * raisonne par matière — c'est l'unité que l'enseignant colore.
   *
   * Par nom de domaine, parce qu'une classe à cheval sur deux cycles reçoit
   * désormais les deux programmes : « Arts plastiques » y figure une fois par
   * cycle, et la carte l'affichait deux fois de suite, à l'identique. C'est le
   * même regroupement que celui du tableau de programmation.
   *
   * Le compte porte sur les sous-domaines DISTINCTS : additionner ferait
   * compter deux fois une notion que les deux cycles partagent.
   */
  const parMatiere = new Map<
    string,
    { couleur: string; domaines: Map<string, { nom: string; sousDomaines: Set<string> }> }
  >();
  for (const d of domaines) {
    const nom = d.subject || d.name;
    const entree = parMatiere.get(nom) ?? { couleur: d.color, domaines: new Map() };
    const domaine = entree.domaines.get(d.name) ?? { nom: d.name, sousDomaines: new Set<string>() };
    for (const sd of d.subdomains) domaine.sousDomaines.add(sd.name);
    entree.domaines.set(d.name, domaine);
    parMatiere.set(nom, entree);
  }

  return (
    <div className="flex flex-col gap-4">
      <Message>
        {'Domaines issus du programme officiel'}
        {niveaux ? ` · ${niveaux}` : ''}
        {'. Modifiez les couleurs pour personnaliser l’affichage dans toute '}
        {'l’application. Le contenu, lui, vient du référentiel et ne se '}
        {'modifie pas ici.'}
      </Message>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[...parMatiere.entries()].map(([nom, m]) => {
          const couleur = couleurs[nom] ?? m.couleur;
          return (
            <Carte key={nom}>
              <div
                className="-m-4 mb-3 flex items-center justify-between gap-2 rounded-t-xl px-4 py-2.5"
                style={{
                  backgroundColor: `${couleur}20`,
                  borderBottom: `2px solid ${couleur}40`,
                }}
              >
                <span className="font-semibold" style={{ color: couleur }}>{nom}</span>
                {/*
                  * `<input type="color">` natif : c'est le sélecteur du système,
                  * celui que l'utilisateur connaît déjà, et il gère seul la
                  * saisie hexadécimale, la pipette et les couleurs récentes.
                  */}
                <input
                  type="color"
                  value={couleur}
                  onChange={(e) => surCouleur(nom, e.target.value)}
                  aria-label={`Couleur de ${nom}`}
                  title="Changer la couleur"
                  className="size-7 cursor-pointer rounded-md border-0 bg-transparent p-0.5"
                />
              </div>

              <ul className="max-h-72 overflow-y-auto">
                {[...m.domaines.values()].map((d) => (
                  <li
                    key={d.nom}
                    className="border-b border-[var(--color-border)] py-1 text-[12px] last:border-0"
                  >
                    <span aria-hidden="true" className="mr-1" style={{ color: couleur }}>▸</span>
                    {d.nom}
                    {d.sousDomaines.size > 0 && (
                      <span className="ml-1 text-[10px] text-[var(--color-fg-muted)]">
                        {`(${d.sousDomaines.size})`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Carte>
          );
        })}
      </div>
    </div>
  );
}
